// Per-event Universe-widget engagement stats for the dashboard cards.
//
// Source: teliance-events' existing universe-widget proxy
// (/api/tenants/<slug>/universe-widget/*). Keyed by the Universe ObjectId,
// which is BOTH the dashboard's event.id AND the widget's listing_id — so we
// match `event.id` ↔ `listing_id` directly (no event_publications/UUID hop).
//
// Two cadences from EventDashboard: live-sessions (liveNow) polled often, and
// funnel-by-event(90d) on a slow cadence to GATE which events show the section.
// The EXPANDED Engagement view drives everything (funnel totals + page views +
// chart) from a single per-event funnel-daily call at the selected range.

import { apiFetch } from '@/lib/api.js';
import { getTenantSlug } from './dashboardContext';

const widgetBase = () => `/api/tenants/${getTenantSlug()}/universe-widget`;

// Live in-flight session count per event (listing_id). Frequent poll.
// Uses the aggregate endpoint (COUNT … GROUP BY, no LIMIT) so the count stays
// correct past 100 concurrent — counting capped rows would cap at 100.
export async function fetchLiveCounts() {
  const r = await apiFetch(`${widgetBase()}/live-count`)
    .then((x) => (x.ok ? x.json() : null)).catch(() => null);
  const counts = {};
  for (const row of r?.listings || []) {
    if (row.listing_id) counts[row.listing_id] = row.count;
  }
  return counts;
}

// Set of event ids (listing_ids) that have ANY widget activity in the window —
// used only to decide which events show the Engagement section. Slow poll.
export async function fetchWidgetEventIds({ days = 90 } = {}) {
  const r = await apiFetch(`${widgetBase()}/funnel-by-event?days=${days}`)
    .then((x) => (x.ok ? x.json() : null)).catch(() => null);
  const ids = new Set();
  for (const l of r?.listings || []) {
    if (l.listing_id && ((l.opens || 0) > 0 || (l.page_views || 0) > 0)) ids.add(l.listing_id);
  }
  return ids;
}

// Per-event daily series for the chosen range (incl. page_views) — drives the
// trend chart only; totals come from fetchWidgetFunnel's rolling window.
// series: [{ day, page_views, opens, built_cart, reached_info, reached_payment, purchased }]
// hourly=true (with days=1): venue-local hour buckets — `day` carries
// 'YYYY-MM-DDTHH:00' labels instead of dates.
export async function fetchWidgetDaily(listingId, days = 7, hourly = false) {
  const gran = hourly ? '&granularity=hour' : '';
  const r = await apiFetch(`${widgetBase()}/funnel-daily?listing_id=${encodeURIComponent(listingId)}&days=${days}${gran}`)
    .then((res) => (res.ok ? res.json() : null)).catch(() => null);
  return r?.series || [];
}

// Per-event ROLLING-window funnel totals (last days×24h, not calendar days) —
// same window semantics as the ticketing-widget analytics page, so "24h"
// shows the same numbers on both. Returns the sumWidgetSeries shape.
export async function fetchWidgetFunnel(listingId, days = 7) {
  const r = await apiFetch(`${widgetBase()}/funnel?listing_id=${encodeURIComponent(listingId)}&days=${days}`)
    .then((res) => (res.ok ? res.json() : null)).catch(() => null);
  const f = r?.funnel;
  if (!f) return null;
  return {
    page_views: f.page_views || 0,
    opens: f.opens || 0,
    cart: f.built_cart || 0,
    info: f.reached_info || 0,
    payment: f.reached_payment || 0,
    purchased: f.purchased || 0,
  };
}

// Sum a daily series into funnel totals keyed to the funnel stages.
export function sumWidgetSeries(series) {
  const t = { page_views: 0, opens: 0, cart: 0, info: 0, payment: 0, purchased: 0 };
  for (const d of series || []) {
    t.page_views += d.page_views || 0;
    t.opens += d.opens || 0;
    t.cart += d.built_cart || 0;
    t.info += d.reached_info || 0;
    t.payment += d.reached_payment || 0;
    t.purchased += d.purchased || 0;
  }
  return t;
}
